import classNames from 'classnames';
import type { StaticImageData } from 'next/image';
import Image from 'next/image';
import { CSSProperties, PropsWithChildren, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import useInterval from '@alltrails/core/hooks/useInterval';
import IconButton from '@alltrails/denali/components/IconButton';
import Pause from '@alltrails/denali/icons/Pause';
import Play from '@alltrails/denali/icons/Play';
import styles from './styles.module.scss';

type Props = {
  buttonContainerStyle?: CSSProperties;
  className: string;
  images: { alt: string; image: StaticImageData }[];
};

const DURATION = 6000;

const RotatingHero = ({ images, className, buttonContainerStyle, children }: PropsWithChildren<Props>) => {
  const intl = useIntl();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [delay, setDelay] = useState<number | null>(DURATION);

  const [prefetchSlides, setPrefetchSlides] = useState(false);

  useEffect(() => {
    const requestIdle: (cb: () => void) => number =
      typeof window.requestIdleCallback === 'function' ? cb => window.requestIdleCallback(cb) : cb => window.setTimeout(cb, 200);
    const cancelIdle: (id: number) => void =
      typeof window.cancelIdleCallback === 'function' ? id => window.cancelIdleCallback(id) : id => window.clearTimeout(id);
    const handle = requestIdle(() => setPrefetchSlides(true));
    return () => cancelIdle(handle);
  }, []);

  const handleSlideClick = (index: number) => {
    // pause or restart image rotation
    setCurrentImageIndex(index);
    setPaused(!paused);
    setDelay(paused ? DURATION : null);
  };

  const handlePlayPauseClick = () => {
    setPaused(!paused);
    setDelay(paused ? DURATION : null);
  };

  useInterval(() => {
    let nextIndex = currentImageIndex + 1;

    if (nextIndex === images.length) {
      nextIndex = 0;
    }

    setCurrentImageIndex(nextIndex);
    setDelay(DURATION);
  }, delay);

  return (
    <section aria-label={intl.formatMessage({ defaultMessage: 'Hero carousel' })}>
      <div className={`${className} ${styles.container}`}>
        {images.map((image, index) => {
          const isFirst = index === 0;
          const hideClass = prefetchSlides ? styles.hidden : styles.none;
          return (
            <Image
              fill
              src={image.image}
              alt={image.alt}
              className={classNames(styles.image, currentImageIndex !== index && hideClass)}
              key={image.image.src}
              loading={isFirst ? 'eager' : 'lazy'}
              preload={isFirst}
              fetchPriority={isFirst ? 'high' : 'auto'}
              sizes="100vw"
            />
          );
        })}
        {children}
        <div className={styles.slideButtons} style={buttonContainerStyle}>
          <IconButton
            className={styles.playPauseButton}
            icon={{ Component: paused ? Play : Pause }}
            onClick={handlePlayPauseClick}
            size="sm"
            title={paused ? intl.formatMessage({ defaultMessage: 'Play slideshow' }) : intl.formatMessage({ defaultMessage: 'Pause slideshow' })}
            ariaLabel={paused ? intl.formatMessage({ defaultMessage: 'Play slideshow' }) : intl.formatMessage({ defaultMessage: 'Pause slideshow' })}
            variant="flat"
          />
          {images.map((image, index) => {
            const progressSpanStyle: CSSProperties = {};
            const innerSpanClassNames: (string | undefined)[] = [];

            if (index < currentImageIndex) {
              innerSpanClassNames.push(styles.slideButtonSpanComplete);
            } else if (index === currentImageIndex) {
              innerSpanClassNames.push(styles.slideButtonSpanActive);
              progressSpanStyle.animationDuration = `${DURATION}ms`;
              progressSpanStyle.animationPlayState = `${paused ? 'paused' : 'running'}`;
            }

            return (
              <button
                aria-label={intl.formatMessage({ defaultMessage: 'Carousel image {index}' }, { index: index + 1 })}
                key={image.alt}
                type="button"
                className={styles.slideButton}
                onClick={() => handleSlideClick(index)}
                aria-current={index === currentImageIndex}
              >
                <span className={styles.slideButtonSpan}>
                  <span className={classNames(innerSpanClassNames)} style={progressSpanStyle} />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RotatingHero;
